import { useCallback, useMemo } from "react";
import { useEffect } from "react";
import { useState } from "react";
import { Input, Button, Row, Col } from "reactstrap";
import _ from "underscore";
import { CheckPicker, SelectPicker, Tooltip, Whisper } from "rsuite";
import pluralize from "pluralize";

import IconButton from "elements/IconButton";
import useFieldMap from "hooks/useFieldMap";
import InputFormatter from "components/Records/Formatters/InputFormatter";
import { variableData, timeData } from "../../utils/DynamicValues";
import FieldUtils from "../../utils/FieldUtils";

const EditPrefillMenu = ({
  module,
  originalModule, // The record's module (when inside the context of a Record, example: SubView)
  prefillPairs, // The prefill pairs
  onChange, // Callback to pass the new prefill pairs to the parent
  toggleEditing,
  showVariables,
  variableIdentifiers = null, // provide array of id's for variables you want
  editOnly,
  customVariableData = null,
  disabledFields, // Array of FieldAPINames to be disabled from adding
  multiSubitemAPIName = null, // Providing this means we will only list the SubitemFields for this MultiSubitem. This changes the logic of this component in various locations.
  fieldSchema, // Provided when dealing with MultiSubitems
}) => {
  // Prefill pairs, but with extra field data top help this component.
  const [sections, setSections] = useState([]);

  // The fields that are already in the prefill pairs.
  const [selectedFields, setSelectedFields] = useState([]);

  const fieldMap = useFieldMap(module?.APIName, "Name");

  // Only relevant when multiSubitemAPIName is provided
  const subitemFields = useMemo(() => {
    if (!module || !multiSubitemAPIName) return null;

    return _.findWhere(module?.Fields, { APIName: multiSubitemAPIName })
      ?.SubitemFields;
  }, [module, multiSubitemAPIName]);

  const constructedVariableData = useMemo(() => {
    let modFieldMap = [];

    if (originalModule) {
      modFieldMap = originalModule?.Fields?.flatMap((field) => {
        // Doesn't currently support multi subitem fields
        if (field.FieldType === "MultiSubitem") return [];

        if (field.FieldType === "Subitem") {
          return field.SubitemFields?.map((subItem) => ({
            id: "record",
            label: `${pluralize.singular(originalModule?.Name)}'s ${
              field.Name
            } - ${subItem.Name}`,
            value: `{{record.${field.APIName}.${subItem.APIName}}}`,
            type: `GrowthCloud ${pluralize.singular(
              originalModule?.Name
            )}'s Data`,
          }));
        }

        return [
          {
            type: `GrowthCloud ${pluralize.singular(
              originalModule?.Name
            )}'s Data`,
            id: "record",
            label: `${pluralize.singular(originalModule?.Name)}'s ${
              field?.Name
            }`,
            value: `{{record.${field?.APIName}}}`,
          },
        ];
      });
    }

    let variables = variableData.concat(modFieldMap);

    const idx = _.findIndex(variables, { value: "{{record.id}}" });

    if (idx >= 0 && originalModule) {
      variables[idx].label = `The ${pluralize.singular(
        originalModule?.Name
      )} (Reference)`;
      variables[idx].type = `GrowthCloud ${pluralize.singular(
        originalModule?.Name
      )}'s Data`;
    }

    return variables;
  }, [originalModule]);

  const handleSectionChange = (newSections) => {
    setSections(newSections);

    onChange(
      newSections.map((s) => {
        return {
          Field: s.APIName,
          Value: s.Value,
        };
      })
    );
  };

  const handleChange = (field, val) => {
    const sectionsCopy = [...sections];
    let fieldAPIName = field.APIName;
    const subitemParent = field.SubitemParent;

    // Handle Subitems (Unless in MultiSubitemAPIName mode)
    if (!multiSubitemAPIName && !!subitemParent) {
      fieldAPIName = `${field.SubitemParent}.${fieldAPIName}`;
    }

    const section = _.find(
      sectionsCopy,
      (section) => section.APIName === fieldAPIName
    );

    section.Value = val;

    handleSectionChange(sectionsCopy);
  };

  const handleStopEditing = () => {
    // First delete any fields that were not given a prefill value.
    for (const section of sections) {
      if (!section.Value) {
        deleteSection(section.APIName);
      }
    }

    toggleEditing();
  };

  const handleVariableToggle = (i) => {
    const copy = [...sections];
    copy[i].usesVariables = !copy[i].usesVariables;
    copy[i].Value = "";
    setSections(copy);
  };

  const addSection = (_, selectedField) => {
    selectedField = selectedField?.APIName;

    if (!selectedField) return;

    // User removed a field from the prefill list.
    if (selectedFields.includes(selectedField)) {
      setSelectedFields((prev) => prev.filter((f) => f !== selectedField));
      setSections((prev) => prev.filter((s) => s.APIName !== selectedField));

      onChange(
        sections
          .filter((s) => s.APIName !== selectedField)
          .map((s) => {
            return {
              Field: s.APIName,
              Value: s.Value,
            };
          })
      );

      return;
    }

    let field;

    // Custom logic for multiSubitemAPIName mode
    if (multiSubitemAPIName) {
      field = subitemFields?.find((f) => f.APIName === selectedField);

      const newSection = {
        APIName: selectedField,
        Field: field,
        Value: "",
      };

      const newSections = [...sections, newSection];

      setSelectedFields((prev) => [...prev, selectedField]);

      handleSectionChange(newSections);

      return;
    }

    const splitString = selectedField.split(".");

    // Handle Subitem Fields, they will have dot separated names.
    if (splitString.length > 1) {
      const parentField = module?.Fields?.find(
        (f) => f.APIName === splitString[0]
      );

      const subitemField = parentField?.SubitemFields?.find(
        (f) => f.APIName === splitString[1]
      );

      field = subitemField;
    } else {
      field = module?.Fields?.find((f) => f.APIName === selectedField);
    }

    const newSection = {
      APIName: selectedField,
      Field: field,
      Value: "",
    };

    const newSections = [...sections, newSection];

    setSelectedFields((prev) => [...prev, selectedField]);

    handleSectionChange(newSections);
  };

  const deleteSection = (APIName) => {
    const newSections = sections.filter(
      (section) => section.APIName !== APIName
    );

    setSelectedFields((prev) => prev.filter((field) => field !== APIName));

    handleSectionChange(newSections);
  };

  const handleExistingPrefill = useCallback(() => {
    if (!prefillPairs || !module) return;

    if (multiSubitemAPIName) {
      const { newSections, newSelectedFields } =
        FieldUtils.handleExistingMultiSubitemPrefill(fieldSchema, prefillPairs);
      setSections(newSections);
      setSelectedFields(newSelectedFields);
    } else {
      const { newSections, newSelectedFields } =
        FieldUtils.handleExistingPrefill(module, prefillPairs);
      setSections(newSections);
      setSelectedFields(newSelectedFields);
    }
  }, [module, prefillPairs, multiSubitemAPIName, fieldSchema]);

  useEffect(() => {
    handleExistingPrefill();
  }, [handleExistingPrefill]);

  const getFieldOptions = () => {
    // Custom logic for MultiSubitemAPIName mode.
    if (multiSubitemAPIName) {
      const foundField = _.findWhere(module?.Fields, {
        APIName: multiSubitemAPIName,
      });

      if (!foundField) {
        console.error("Couldn't find MultiSubitem in EditPrefillMenu!");
        return [];
      }

      return foundField?.SubitemFields?.flatMap((f) => {
        if (
          Array.isArray(disabledFields) &&
          disabledFields.includes(f?.FieldType)
        )
          return [];

        return {
          Name: f.Name,
          APIName: f.APIName,
        };
      });
    }

    return module?.Fields?.flatMap((field) => {
      if (field.Name === "Remote Systems") return [];

      if (
        Array.isArray(disabledFields) &&
        disabledFields.includes(field?.FieldType)
      ) {
        return [];
      }

      const subitemOptions = FieldUtils.getSubitemOptions(
        field,
        (name, value, group) => ({
          Name: name,
          APIName: value,
          ...(group ? { Group: group } : {}),
        })
      );

      if (subitemOptions) return subitemOptions;

      return {
        Name: field.Name,
        APIName: field.APIName,
        Group: "Base Fields",
      };
    });
  };

  const data = getFieldOptions();

  return (
    <>
      {!editOnly && (
        <div className="d-flex justify-content-between">
          <h4>Editing Prefill</h4>
          <Button size="sm" onClick={handleStopEditing}>
            Stop Editing
          </Button>
        </div>
      )}
      <h5>Add A Field</h5>
      <div className="d-flex w-100">
        <CheckPicker
          className="w-100"
          preventOverflow
          data={data}
          labelKey="Name"
          valueKey="APIName"
          groupBy={multiSubitemAPIName ? undefined : "Group"}
          onSelect={addSection}
          value={selectedFields || []}
          onClean={() => {
            setSelectedFields([]);
            setSections([]);
            onChange([]);
          }}
          placeholder="Select a Field"
        />
      </div>
      <hr className="mt-4 mb-0"></hr>
      {sections.map((section, i) => (
        <div key={section.APIName} className="my-3">
          <h5>
            {!multiSubitemAPIName &&
              !!section.Field?.SubitemParent &&
              fieldMap[section.Field?.SubitemParent] + " "}
            {section.Field?.Name}
          </h5>
          <Row>
            <Col style={{ maxWidth: "calc(100% - 91px)" }}>
              {showVariables && section.usesVariables ? (
                <SelectPicker
                  groupBy={
                    !customVariableData &&
                    section.Field.FieldType === "DateTime"
                      ? undefined
                      : "type"
                  }
                  placement="autoVertical"
                  data={
                    customVariableData
                      ? customVariableData
                      : section.Field.FieldType === "DateTime"
                      ? timeData
                      : !variableIdentifiers
                      ? constructedVariableData
                      : constructedVariableData.filter((data) =>
                          variableIdentifiers.includes(data.id)
                        )
                  }
                  style={{ width: "100%" }}
                  placeholder="Choose A Dynamic Value"
                  onChange={(val) => handleChange(section.Field, val)}
                  value={section.Value}
                />
              ) : (
                <InputFormatter
                  module={module}
                  field={section.Field}
                  value={section.Value}
                  onChange={handleChange}
                />
              )}
            </Col>
            <Col xs="auto" className="pl-0">
              {showVariables && (
                <Whisper
                  placement="left"
                  trigger="hover"
                  speaker={
                    <Tooltip style={{ minWidth: 150 }}>
                      {section.usesVariables
                        ? "Enter your own value"
                        : "Use a dynamic value"}
                    </Tooltip>
                  }
                >
                  <Button
                    size="sm"
                    outline={!section.usesVariables}
                    onClick={() => handleVariableToggle(i)}
                    className="btn-icon-only"
                    color="secondary"
                  >
                    <i className="mdi mdi-xml"></i>
                  </Button>
                </Whisper>
              )}
              <IconButton
                onClick={() => deleteSection(section.APIName)}
                tooltipText="Remove field"
                color="danger"
                icon="close"
              />
            </Col>
          </Row>
        </div>
      ))}
    </>
  );
};

export default EditPrefillMenu;
